/**
 * PortalRegistro (/portal/registro) — legacy redirect.
 *
 * Ahora redirige a /portal/complete-profile preservando todos los query params.
 * Se mantiene por compatibilidad con planes de MercadoPago cuyo back_url
 * fue configurado en el panel de MP apuntando a esta ruta antes del cambio de
 * DEFAULT_SUCCESS_PATH. Los nuevos checkouts ya van directo a /portal/complete-profile.
 *
 * Código de registro original (Stripe + MercadoPago redirigen acá con session_id/payment_id):
 *
 *   ┌─────────────────────────────────────────┐
 *   │  Mi nombre es (opcional)                │
 *   │  Mi apellido es (opcional)              │
 *   │  Mi email es * ──────────────  validar  │
 *   │  Mi usuario de Telegram es * @miusuario │
 *   │  Mi cumpleaños es * 📅                  │
 *   └─────────────────────────────────────────┘
 *           ↓ submit
 *   Clerk signUp.create (sin password) → email verification code
 *   → /portal (estado pending; admin aprueba y manda URL personal por email)
 *
 * Auth passwordless: Clerk solo necesita email + código verification. Login
 * futuro = email + magic code. La instancia Clerk debe tener "Email code"
 * como sign-in strategy + password marcado como optional/off.
 *
 * Copy bilingüe ES/EN (useLanguage decide). i18n inline en COPY_ES / COPY_EN.
 * Telegram alias: OBLIGATORIO (el equipo lo usa para mandar el invite).
 * Fecha de nacimiento: input type=date nativo (mobile-friendly).
 * Custom data va a Clerk `unsafeMetadata` (la lee /api/admin/memberships).
 */

import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, useSignUp } from "@clerk/clerk-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@heroui/react";
import {
  AlertTriangle,
  CalendarDays,
  Cake,
  CheckCircle2,
  Loader2,
  Mail,
  Send,
  ShieldCheck,
  User,
  UserPlus,
} from "lucide-react";

import { useSEO } from "@/hooks/useSEO";
import { useLanguage } from "@/hooks/useLanguage";
import { BRAND_PERSON_NAME, BRAND_SITE_URL, BRAND_OG_IMAGE_URL } from "@/lib/brandConfig";
import { MEMBERSHIP_ROUTE } from "@/lib/membershipAccessFlow";
import { captureAttribution } from "@/lib/captureAttribution";
import {
  buildMembershipProfileMetadata,
  deriveMembershipAlias,
} from "../../shared/membershipClerkMetadata";

// ── i18n inline ──────────────────────────────────────────────────────────────
// Página crítica del flow post-pago. ES para AR/LATAM, EN para US/intl.
// Inline porque ningún otro archivo reusa estas cadenas y meterlas en locales/
// suma carga sin beneficio. Source of truth única.
type PortalCopy = {
  seoTitle: string;
  seoDescription: string;
  headingNew: string;
  headingVerify: string;
  descPaid: string;
  descNoPayment: string;
  descVerify: (email: string) => string;
  paymentConfirmedTitle: string;
  paymentConfirmedBody: string;
  approvalNotice: string;
  alreadyHaveAccount: string;
  enterHere: string;
  verifyCodeLabel: string;
  verifyButton: string;
  firstName: string;
  firstNamePlaceholder: string;
  lastName: string;
  lastNamePlaceholder: string;
  optional: string;
  email: string;
  emailPlaceholder: string;
  emailInvalid: string;
  telegram: string;
  telegramPlaceholder: string;
  telegramExplain: string;
  telegramInvalid: string;
  birthday: string;
  birthdayAge: (n: number) => string;
  submitButton: string;
  validationError: string;
  signUpFailed: string;
  verifyCodeMismatch: string;
  verifyCodeInvalid: string;
};

const COPY_ES: PortalCopy = {
  seoTitle: "Mi registro",
  seoDescription: `Completo mi registro para mi membresía con ${BRAND_PERSON_NAME}.`,
  headingNew: "Soy nueva acá",
  headingVerify: "Confirmo mi email",
  descPaid: `Mi pago se recibió. Ahora completo mis datos para que el equipo me apruebe y me envíe mi acceso personal a ${BRAND_PERSON_NAME}.`,
  descNoPayment: `Completo mis datos para sumarme como miembro de ${BRAND_PERSON_NAME}. El equipo aprueba mi solicitud y me envía mi acceso personal.`,
  descVerify: (email) => `Ingreso el código que recibí en ${email} para activar mi cuenta.`,
  paymentConfirmedTitle: "Mi pago está confirmado.",
  paymentConfirmedBody:
    " Completo este formulario y entro a la lista de aprobación. Recibo mi link personal en mi email apenas el equipo me apruebe.",
  approvalNotice: "Mi acceso al canal/grupo privado requiere una aprobación manual del equipo.",
  alreadyHaveAccount: "¿Ya tengo cuenta?",
  enterHere: "Entro acá",
  verifyCodeLabel: "Mi código de verificación",
  verifyButton: "Confirmo mi email",
  firstName: "Mi nombre",
  firstNamePlaceholder: "Sofía",
  lastName: "Mi apellido",
  lastNamePlaceholder: "Pérez",
  optional: "(opcional)",
  email: "Mi email",
  emailPlaceholder: "soyfan@email.com",
  emailInvalid: "Reviso mi email — falta el @ o el dominio.",
  telegram: "Mi usuario de Telegram",
  telegramPlaceholder: "miusuario",
  telegramExplain: "Lo necesito para que el equipo me envíe mi link personal de acceso al canal/grupo.",
  telegramInvalid: "Mi usuario debe tener entre 5 y 32 caracteres (letras, números o guión bajo).",
  birthday: "Mi cumpleaños",
  birthdayAge: (n) => `Tengo ${n} años — ¡gracias por sumarte!`,
  submitButton: "Creo mi cuenta",
  validationError: "Reviso los campos: email, Telegram (@usuario válido) y mi cumpleaños son obligatorios.",
  signUpFailed: "No pude completar mi alta. Reviso mis datos y reintento.",
  verifyCodeMismatch: "Mi código no coincide. Reviso mi email y reintento.",
  verifyCodeInvalid: "Código inválido. Reintento.",
};

const COPY_EN: PortalCopy = {
  seoTitle: "My registration",
  seoDescription: `Complete my registration for ${BRAND_PERSON_NAME}'s exclusive membership.`,
  headingNew: "I'm new here",
  headingVerify: "Confirm my email",
  descPaid: `Payment received. Now I complete my details so the team can approve me and send my personal access to ${BRAND_PERSON_NAME}.`,
  descNoPayment: `Complete my details to join as a member of ${BRAND_PERSON_NAME}. The team reviews my request and sends my personal access.`,
  descVerify: (email) => `Enter the code I received at ${email} to activate my account.`,
  paymentConfirmedTitle: "Payment confirmed.",
  paymentConfirmedBody:
    " I fill in this form and join the approval queue. I'll receive my personal access link via email as soon as the team approves me.",
  approvalNotice: "Access to the private channel/group requires manual team approval.",
  alreadyHaveAccount: "Already have an account?",
  enterHere: "Sign in",
  verifyCodeLabel: "My verification code",
  verifyButton: "Confirm my email",
  firstName: "First name",
  firstNamePlaceholder: "Jane",
  lastName: "Last name",
  lastNamePlaceholder: "Doe",
  optional: "(optional)",
  email: "Email",
  emailPlaceholder: "fan@email.com",
  emailInvalid: "Check the email — missing @ or domain.",
  telegram: "My Telegram username",
  telegramPlaceholder: "myhandle",
  telegramExplain: "Required so the team can send my personal access link to the private channel/group.",
  telegramInvalid: "Telegram username must be 5-32 chars (letters, numbers or underscore).",
  birthday: "My birthday",
  birthdayAge: (n) => `I'm ${n} — thanks for joining!`,
  submitButton: "Create my account",
  validationError: "Check the fields: email, Telegram (@valid username) and birthday are required.",
  signUpFailed: "Couldn't complete the sign-up. I'll review the data and try again.",
  verifyCodeMismatch: "Code doesn't match. I'll check my email and retry.",
  verifyCodeInvalid: "Invalid code. Retry.",
};

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Normaliza un alias de Telegram a `@usuario`, sin espacios. */
function normalizeTelegramAlias(raw: string): string {
  const cleaned = raw.trim().replace(/^@+/, "").replace(/\s+/g, "");
  if (!cleaned) return "";
  return `@${cleaned}`;
}

/** Telegram exige 5-32 chars, alphanumeric + underscore (sin @). */
function isValidTelegramAlias(alias: string): boolean {
  const bare = alias.replace(/^@/, "");
  return /^[A-Za-z0-9_]{5,32}$/.test(bare);
}

/** "Mi cumpleaños fue hace X años" — para mostrar edad en vivo */
function calcAge(isoDate: string): number | null {
  if (!isoDate) return null;
  const birth = new Date(isoDate);
  if (Number.isNaN(birth.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const monthDiff = now.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate())) age -= 1;
  return age;
}

/**
 * Genera un secret aleatorio para satisfacer auth_config.password="required" de
 * la instancia Clerk. El usuario nunca lo ve — login futuro es vía email code.
 * Garantiza upper + lower + digit + symbol (Clerk default = score ≥3) y 32 chars
 * para entropía ~190 bits.
 */
function generatePasswordlessSecret(): string {
  const buf = new Uint8Array(24);
  crypto.getRandomValues(buf);
  const b64 = btoa(String.fromCharCode(...buf))
    .replace(/=+$/, "")
    .replace(/\+/g, "A")
    .replace(/\//g, "z");
  // Garantizamos las 4 clases sin importar lo que devuelva el random.
  return `Q1!${b64}9z`;
}

/** Min date = hoy menos 100 años, max date = hoy menos 18 años (validación 18+). */
const TODAY = new Date();
const MAX_BIRTH_DATE = new Date(TODAY.getFullYear() - 18, TODAY.getMonth(), TODAY.getDate())
  .toISOString().split("T")[0];
const MIN_BIRTH_DATE = new Date(TODAY.getFullYear() - 100, 0, 1)
  .toISOString().split("T")[0];

// ── Componente ───────────────────────────────────────────────────────────────
// Redirect shell: preserva query string para que session_id, payment_id,
// preapproval_id, etc. lleguen intactos a /portal/complete-profile.

export default function PortalRegistro() {
  const { search } = useLocation();
  return <Navigate to={`/portal/complete-profile${search}`} replace />;
}

// ── Legacy implementation (kept for reference, no longer rendered) ────────────
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function PortalRegistroLegacy() {
  // Idioma efectivo del visitor (en para US/intl, es para AR/LATAM).
  // currentLanguage es el código normalizado "en"/"es" (sin variante BCP47).
  const { currentLanguage } = useLanguage();
  const t = currentLanguage === "en" ? COPY_EN : COPY_ES;

  useSEO({
    title: t.seoTitle,
    description: t.seoDescription,
    keywords: "",
    ogImage: BRAND_OG_IMAGE_URL,
    canonical: `${BRAND_SITE_URL}/portal/registro`,
    robots: "noindex, nofollow",
  });
  const navigate = useNavigate();
  const { search } = useLocation();
  const [params] = useSearchParams();
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const { isLoaded: signUpLoaded, signUp, setActive } = useSignUp();

  // Pagos: si el visitor viene de Stripe (session_id) o MP (payment_id /
  // collection_id / preapproval_id), preservamos los IDs en metadata para
  // que admin/memberships pueda matchear la membresía contra la transacción.
  const paymentIds = useMemo(() => {
    return {
      stripe_session_id:        params.get("session_id") || undefined,
      stripe_payment_intent_id: params.get("payment_intent") || undefined,
      stripe_subscription_id:   params.get("subscription_id") || undefined,
      mp_payment_id:            params.get("payment_id") || params.get("collection_id") || undefined,
      mp_preapproval_id:        params.get("preapproval_id") || undefined,
      plan_tier:                (params.get("plan_tier") || "").trim().toLowerCase() || undefined,
      gateway:
        params.get("provider") ||
        (params.get("session_id") ? "stripe" : undefined) ||
        (params.get("payment_id") || params.get("collection_id") || params.get("preapproval_id") ? "mercadopago" : undefined),
    };
  }, [params]);
  const hasPaymentSignal = Boolean(
    paymentIds.stripe_session_id ||
    paymentIds.mp_payment_id ||
    paymentIds.mp_preapproval_id ||
    paymentIds.stripe_subscription_id
  );

  // ── PostHog tracking helper ────────────────────────────────────────────────
  // posthog se inicializa en main.tsx (window.posthog). Helper safe: no rompe
  // si posthog no cargó (ad-blockers, env vacío, primer paint, etc.).
  const track = (event: string, props: Record<string, unknown> = {}) => {
    if (typeof window === "undefined") return;
    const ph = (window as Window & {
      posthog?: { capture: (e: string, p?: object) => void };
    }).posthog;
    ph?.capture(event, {
      ...props,
      has_payment_signal: hasPaymentSignal,
      gateway: paymentIds.gateway,
      plan_tier: paymentIds.plan_tier,
      language: currentLanguage,
    });
  };

  // View event al mount — el visitor llegó del checkout o vía link directo.
  // Sin esto el funnel post-pago (Stripe success_url → registro → portal)
  // queda dark para PostHog.
  useEffect(() => {
    track("portal_registro_view");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Form state — todo primera persona en placeholders.
  const [firstName, setFirstName]       = useState("");
  const [lastName, setLastName]         = useState("");
  const [email, setEmail]               = useState("");
  const [telegramRaw, setTelegramRaw]   = useState("");
  const [birthDate, setBirthDate]       = useState("");

  const [submitting, setSubmitting]     = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [verifyStep, setVerifyStep]     = useState(false);
  const [verifyCode, setVerifyCode]     = useState("");
  const [verifying, setVerifying]       = useState(false);

  const telegramAlias = useMemo(() => normalizeTelegramAlias(telegramRaw), [telegramRaw]);
  const telegramValid = telegramAlias ? isValidTelegramAlias(telegramAlias) : false;
  const age = useMemo(() => calcAge(birthDate), [birthDate]);
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const formValid =
    emailOk && telegramValid && birthDate.length > 0;

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signUpLoaded || !signUp || submitting) return;
    if (!formValid) {
      track("portal_registro_validation_failed", {
        has_email: emailOk,
        has_telegram: telegramValid,
        has_birthday: Boolean(birthDate),
      });
      setError(t.validationError);
      return;
    }
    setError(null);
    setSubmitting(true);
    track("portal_registro_submitted", {
      has_first_name: Boolean(firstName.trim()),
      has_last_name: Boolean(lastName.trim()),
    });
    // attribution_events: submit del registro → reporte mensual del admin
    // (event_name 'form_submitted'). best-effort, no bloquea el alta.
    captureAttribution("form_submitted", {
      metadata: { form: "portal_registro" },
    });

    try {
      // unsafeMetadata = leído por /api/admin/memberships para mostrar al admin
      // los datos extra (telegram, birthdate, payment IDs) sin tocar otra tabla.
      const derivedAlias = deriveMembershipAlias({
        firstName,
        lastName,
        telegram: telegramAlias,
        email,
      });
      const unsafeMetadata = buildMembershipProfileMetadata(
        { alias: derivedAlias, telegram: telegramAlias, birthDate },
        {
        ...(paymentIds.stripe_session_id        && { stripe_session_id: paymentIds.stripe_session_id }),
        ...(paymentIds.stripe_payment_intent_id && { stripe_payment_intent_id: paymentIds.stripe_payment_intent_id }),
        ...(paymentIds.stripe_subscription_id   && { stripe_subscription_id: paymentIds.stripe_subscription_id }),
        ...(paymentIds.mp_payment_id            && { mp_payment_id: paymentIds.mp_payment_id }),
        ...(paymentIds.mp_preapproval_id        && { mp_preapproval_id: paymentIds.mp_preapproval_id }),
        ...(paymentIds.plan_tier                && { plan_tier: paymentIds.plan_tier }),
        ...(paymentIds.gateway                  && { gateway: paymentIds.gateway }),
        membership_status: "pending_approval",
        registered_via:    "portal_registro_v1",
        },
      );

      // Passwordless desde la perspectiva del usuario: en pantalla solo entra
      // email + Telegram + birthday. Clerk valida vía código por email
      // (email_code first-factor). Login futuro = email + magic code, nunca
      // requiere contraseña al usuario.
      //
      // Fallback de password random: la instancia Clerk de juaperrone tiene
      // auth_config.password="required" (verificado vía Frontend API). Hasta
      // que el admin lo flippee a "off" en el dashboard, signUp.create()
      // necesita un password para no tirar "password is required". Generamos
      // un secret cliente-side (32+ chars con upper/lower/digits/symbol) que
      // jamás se le muestra al usuario y nunca se guarda — se descarta apenas
      // sale del scope. El usuario solo conoce el código email.
      //
      // Cuando flipees el toggle de Clerk a password=off, remové estas 8 líneas
      // (declaración + spread del campo password) sin tocar nada más.
      const randomPassword = generatePasswordlessSecret();
      await signUp.create({
        emailAddress: email,
        password: randomPassword,
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        unsafeMetadata,
      });

      // Si Clerk pide email verification → mostramos el step.
      // Si no (instance config sin verification), session ya está creada.
      const needsEmailVerify =
        signUp.unverifiedFields?.includes("email_address");
      if (needsEmailVerify) {
        await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
        track("portal_registro_verify_step_shown");
        setVerifyStep(true);
      } else {
        track("portal_registro_completed", { skipped_verify: true });
        // Activar la sesión y redirigir al portal (pending).
        if (signUp.createdSessionId) {
          await setActive({ session: signUp.createdSessionId });
        }
        navigate(`${MEMBERSHIP_ROUTE.PORTAL}${search}`, { replace: true });
      }
    } catch (err) {
      const msg =
        err && typeof err === "object" && "errors" in err
          ? (err as { errors?: Array<{ message?: string; longMessage?: string }> })
              .errors?.[0]?.longMessage ??
            (err as { errors?: Array<{ message?: string }> }).errors?.[0]?.message ??
            t.signUpFailed
          : t.signUpFailed;
      track("portal_registro_signup_error", { message: String(msg).slice(0, 200) });
      setError(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signUpLoaded || !signUp || verifying) return;
    setError(null);
    setVerifying(true);
    track("portal_registro_verify_submitted");
    try {
      const result = await signUp.attemptEmailAddressVerification({ code: verifyCode });
      if (result.status === "complete" && result.createdSessionId) {
        track("portal_registro_completed", { skipped_verify: false });
        await setActive({ session: result.createdSessionId });
        navigate(`${MEMBERSHIP_ROUTE.PORTAL}${search}`, { replace: true });
      } else {
        track("portal_registro_verify_mismatch");
        setError(t.verifyCodeMismatch);
      }
    } catch (err) {
      const msg =
        err && typeof err === "object" && "errors" in err
          ? (err as { errors?: Array<{ message?: string }> }).errors?.[0]?.message ??
            t.verifyCodeInvalid
          : t.verifyCodeInvalid;
      track("portal_registro_verify_error", { message: String(msg).slice(0, 200) });
      setError(String(msg));
    } finally {
      setVerifying(false);
    }
  };

  // ── Auto-prefill email si vino de Stripe customer_email en query ──────────
  useEffect(() => {
    const prefillEmail = params.get("email")?.trim();
    if (prefillEmail && !email) setEmail(prefillEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // Si ya está logueado, lo mando al portal (la pantalla de pendiente). Va
  // DESPUÉS de los hooks para no violar rules-of-hooks (early-return cambia
  // el orden de invocación entre renders).
  if (authLoaded && isSignedIn) {
    return <Navigate to={`${MEMBERSHIP_ROUTE.PORTAL}${search}`} replace />;
  }

  if (!authLoaded || !signUpLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-zinc-950 text-zinc-100 flex items-center justify-center px-4 py-10 sm:py-12">
      {/* Ambient glow */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-amber-500/15 blur-[120px]" />
        <div className="absolute bottom-[-10rem] right-[-6rem] h-80 w-80 rounded-full bg-amber-400/10 blur-[130px]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(255,255,255,0.04),_transparent_60%)]" />
      </div>

      <div className="relative mx-auto w-full max-w-md">
        <Card className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-2xl shadow-black/60 ring-1 ring-inset ring-white/[0.06]">
          <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

          <CardHeader className="text-center space-y-3 pb-4 pt-7">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-amber-300/20 bg-amber-500/15 shadow-inner shadow-amber-500/10 backdrop-blur">
              <UserPlus className="h-7 w-7 text-amber-300" />
            </div>
            <CardTitle className="text-2xl font-semibold tracking-tight text-zinc-50">
              {verifyStep ? t.headingVerify : t.headingNew}
            </CardTitle>
            <CardDescription className="mx-auto max-w-sm text-sm leading-relaxed text-zinc-400">
              {verifyStep
                ? t.descVerify(email)
                : hasPaymentSignal
                  ? t.descPaid
                  : t.descNoPayment}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4 px-5 pb-6 pt-1 sm:px-6">
            {hasPaymentSignal && !verifyStep && (
              <div className="flex items-start gap-2 rounded-2xl border border-emerald-500/25 bg-emerald-500/10 px-4 py-3 text-xs leading-relaxed text-emerald-100 backdrop-blur">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />
                <p>
                  <strong className="text-emerald-200">{t.paymentConfirmedTitle}</strong>
                  {t.paymentConfirmedBody}
                </p>
              </div>
            )}

            {error && (
              <div className="flex items-start gap-2 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-xs leading-relaxed text-rose-100">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-300" />
                <p>{error}</p>
              </div>
            )}

            {verifyStep ? (
              <form onSubmit={handleVerify} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="verify-code" className="text-zinc-300">
                    {t.verifyCodeLabel}
                  </Label>
                  <Input
                    id="verify-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
                    className="bg-black/30 border-white/10 text-zinc-100 text-center text-lg tracking-[0.4em] font-mono h-12"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  color="warning"
                  className="w-full h-12 bg-amber-500 text-amber-950 font-semibold"
                  isLoading={verifying}
                  isDisabled={verifyCode.length < 4}
                >
                  {t.verifyButton}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Nombre + Apellido (opcionales) */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="first-name" className="flex items-center gap-1.5 text-zinc-300 text-xs">
                      <User className="h-3 w-3" /> {t.firstName}
                      <span className="text-zinc-600">{t.optional}</span>
                    </Label>
                    <Input
                      id="first-name"
                      type="text"
                      autoComplete="given-name"
                      placeholder={t.firstNamePlaceholder}
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="bg-black/30 border-white/10 text-zinc-100 h-11"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="last-name" className="text-zinc-300 text-xs">
                      {t.lastName}
                      <span className="text-zinc-600 ml-1">{t.optional}</span>
                    </Label>
                    <Input
                      id="last-name"
                      type="text"
                      autoComplete="family-name"
                      placeholder={t.lastNamePlaceholder}
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="bg-black/30 border-white/10 text-zinc-100 h-11"
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="flex items-center gap-1.5 text-zinc-300 text-xs">
                    <Mail className="h-3 w-3" /> {t.email}
                    <span className="text-amber-400">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder={t.emailPlaceholder}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-black/30 border-white/10 text-zinc-100 h-11"
                    required
                  />
                  {!emailOk && email.length > 0 && (
                    <p className="text-[11px] text-rose-400">{t.emailInvalid}</p>
                  )}
                </div>

                {/* Telegram alias — obligatorio */}
                <div className="space-y-1.5">
                  <Label htmlFor="telegram" className="flex items-center gap-1.5 text-zinc-300 text-xs">
                    <Send className="h-3 w-3" /> {t.telegram}
                    <span className="text-amber-400">*</span>
                  </Label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-3 flex items-center text-zinc-500 text-sm pointer-events-none">@</span>
                    <Input
                      id="telegram"
                      type="text"
                      autoComplete="off"
                      placeholder={t.telegramPlaceholder}
                      value={telegramRaw.replace(/^@/, "")}
                      onChange={(e) => setTelegramRaw(e.target.value)}
                      className="bg-black/30 border-white/10 text-zinc-100 h-11 pl-7"
                      required
                    />
                  </div>
                  <p className="text-[11px] text-zinc-500">
                    {t.telegramExplain}
                  </p>
                  {telegramRaw && !telegramValid && (
                    <p className="text-[11px] text-rose-400">
                      {t.telegramInvalid}
                    </p>
                  )}
                </div>

                {/* Fecha de nacimiento — input date nativo */}
                <div className="space-y-1.5">
                  <Label htmlFor="birth-date" className="flex items-center gap-1.5 text-zinc-300 text-xs">
                    <Cake className="h-3 w-3" /> {t.birthday}
                    <span className="text-amber-400">*</span>
                  </Label>
                  <div className="relative">
                    <CalendarDays className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500 pointer-events-none" />
                    <Input
                      id="birth-date"
                      type="date"
                      value={birthDate}
                      onChange={(e) => setBirthDate(e.target.value)}
                      min={MIN_BIRTH_DATE}
                      max={MAX_BIRTH_DATE}
                      className="bg-black/30 border-white/10 text-zinc-100 h-11 pr-10"
                      required
                    />
                  </div>
                  {age != null && (
                    <p className="text-[11px] text-amber-300/80">
                      {t.birthdayAge(age)}
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  color="warning"
                  className="w-full h-12 bg-amber-500 text-amber-950 font-semibold mt-2"
                  isLoading={submitting}
                  isDisabled={!formValid || submitting}
                >
                  {t.submitButton}
                </Button>
              </form>
            )}

            <div className="pt-2 border-t border-white/10 flex items-center gap-2 text-xs text-zinc-500">
              <ShieldCheck className="w-4 h-4 shrink-0" />
              <span>{t.approvalNotice}</span>
            </div>

            <p className="text-[11px] text-zinc-600 text-center pt-1">
              {t.alreadyHaveAccount}{" "}
              <a
                href={`${MEMBERSHIP_ROUTE.ENTRAR}${search}`}
                className="text-amber-400 hover:text-amber-300 underline"
              >
                {t.enterHere}
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
